import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, feedAPI, wallAPI } from './api';
import { clearCache } from './dataCache';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [unreadFeedCount, setUnreadFeedCount] = useState(0);
    const [wallUnread, setWallUnread] = useState({ total_unread_messages: 0, total_unread_dossiers: 0, items: [] });

    // Rafraîchir le compteur de publications non lues (pastille "Fil d'actualité")
    const refreshUnreadFeedCount = useCallback(async () => {
        try {
            const res = await feedAPI.getUnreadCount();
            setUnreadFeedCount(res.data?.unread_count ?? 0);
        } catch {
            // Silencieux : ne bloque pas l'UI
        }
    }, []);

    // Rafraîchir la synthèse des murs non lus (pastilles + encart Dashboard)
    const refreshWallUnread = useCallback(async () => {
        try {
            const res = await wallAPI.unreadSummary();
            setWallUnread(res.data || { total_unread_messages: 0, total_unread_dossiers: 0, items: [] });
        } catch {
            // Silencieux : ne bloque jamais l'UI / le Dashboard
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('valnexo_token');
        const savedUser = localStorage.getItem('valnexo_user');
        
        if (token && savedUser) {
            setUser(JSON.parse(savedUser));
            // Verify token is still valid
            authAPI.me()
                .then((res) => {
                    setUser(res.data);
                    localStorage.setItem('valnexo_user', JSON.stringify(res.data));
                })
                .catch((err) => {
                    // Déconnecter UNIQUEMENT si le serveur a explicitement rejeté le token (401)
                    // PAS sur erreur réseau ou timeout (le retry dans api.js aura déjà tenté)
                    if (err.response?.status === 401) {
                        logout();
                    }
                    // Sur erreur réseau, garder la session locale
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    // Auto-refresh du profil utilisateur pour propager rapidement les changements
    // de droits (activation `is_admin`, changement de rôle, désactivation…) sans
    // avoir à faire une déco/reco manuelle.
    // - Toutes les 3 minutes tant que l'utilisateur est connecté
    // - Immédiatement au retour de l'onglet (visibilitychange → active)
    // - Immédiatement au retour de la fenêtre au focus
    useEffect(() => {
        if (!user) return;

        const refreshSilently = async () => {
            try {
                const res = await authAPI.me();
                const fresh = res.data;
                setUser((prev) => {
                    // Ne remplace que si le serveur a effectivement changé quelque chose
                    // (évite un re-render inutile qui ferait clignoter l'UI).
                    if (!prev || JSON.stringify(prev) !== JSON.stringify(fresh)) {
                        localStorage.setItem('valnexo_user', JSON.stringify(fresh));
                        return fresh;
                    }
                    return prev;
                });
            } catch (_) {
                // Silencieux — best effort. Un vrai 401 sera déjà géré par l'intercepteur axios.
            }
        };

        const interval = setInterval(refreshSilently, 3 * 60 * 1000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') refreshSilently();
        };
        window.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refreshSilently);

        return () => {
            clearInterval(interval);
            window.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', refreshSilently);
        };
    }, [user]);

    // Compteur "Fil d'actualité" : chargement initial + rafraîchissement périodique
    useEffect(() => {
        if (!user) {
            setUnreadFeedCount(0);
            setWallUnread({ total_unread_messages: 0, total_unread_dossiers: 0, items: [] });
            return;
        }
        refreshUnreadFeedCount();
        refreshWallUnread();
        const interval = setInterval(refreshUnreadFeedCount, 60000);
        const wallInterval = setInterval(refreshWallUnread, 60000);
        return () => { clearInterval(interval); clearInterval(wallInterval); };
    }, [user, refreshUnreadFeedCount, refreshWallUnread]);

    const login = async (email, password) => {
        const response = await authAPI.login({ email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('valnexo_token', access_token);
        localStorage.setItem('valnexo_user', JSON.stringify(userData));
        setUser(userData);
        
        return response.data; // Return full response for biometric
    };

    // Set auth data from biometric reauth (token already stored)
    const setAuthData = async (token) => {
        localStorage.setItem('valnexo_token', token);
        try {
            const res = await authAPI.me();
            setUser(res.data);
            localStorage.setItem('valnexo_user', JSON.stringify(res.data));
            return res.data;
        } catch (error) {
            logout();
            throw error;
        }
    };

    const register = async (data) => {
        const response = await authAPI.register(data);
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('valnexo_token', access_token);
        localStorage.setItem('valnexo_user', JSON.stringify(userData));
        setUser(userData);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('valnexo_token');
        localStorage.removeItem('valnexo_user');
        clearCache();
        setUser(null);
    };

    // Rafraîchir les données utilisateur depuis le serveur
    const refreshUser = async () => {
        try {
            const res = await authAPI.me();
            setUser(res.data);
            localStorage.setItem('valnexo_user', JSON.stringify(res.data));
            return res.data;
        } catch (error) {
            console.error('Error refreshing user:', error);
            return null;
        }
    };

    // Mettre à jour l'utilisateur localement (sans appel serveur)
    const updateUser = (updatedFields) => {
        const newUser = { ...user, ...updatedFields };
        setUser(newUser);
        localStorage.setItem('valnexo_user', JSON.stringify(newUser));
    };

    const isAdmin = () => {
        return user?.is_admin === true || user?.role === 'admin_full' || user?.role === 'admin';
    };

    const isAdminFull = () => {
        return user?.is_admin === true || user?.role === 'admin_full';
    };

    const isVP = () => {
        return user?.role === 'vp';
    };

    const isApporteur = () => {
        return user?.role === 'apporteur';
    };

    // Candidat en onboarding : accès Académie uniquement
    const isApprenant = () => {
        return user?.role === 'apprenant';
    };

    // Community Manager : accès Inside VNX uniquement (aucun accès CRM)
    const isCommunity = () => {
        return user?.role === 'community';
    };

    // Rôle Auditeur (flag additif, garde le rôle principal)
    const isAuditor = () => {
        return user?.is_auditor === true;
    };

    // Accès CRM limité (apporteur insuffisamment formé : Académie + Support uniquement)
    const isTrainingRestricted = () => {
        return user?.role === 'apporteur' && ['restricted', 'disabled'].includes(user?.training_access_level);
    };

    // Get token for WebSocket connections
    const getToken = () => {
        return localStorage.getItem('valnexo_token');
    };

    const value = {
        user,
        loading,
        token: localStorage.getItem('valnexo_token'),
        login,
        register,
        logout,
        refreshUser,
        updateUser,
        setAuthData,
        getToken,
        isAdmin,
        isAdminFull,
        isVP,
        isApporteur,
        isApprenant,
        isCommunity,
        isAuditor,
        isTrainingRestricted,
        unreadFeedCount,
        refreshUnreadFeedCount,
        wallUnread,
        wallUnreadCount: wallUnread.total_unread_dossiers || 0,
        wallUnreadMessages: wallUnread.total_unread_messages || 0,
        wallUnreadItems: wallUnread.items || [],
        refreshWallUnread,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
